import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Column, Options, Action } from 'material-table';
import { Button, Checkbox, createStyles, FormControlLabel, Grid, Icon, makeStyles, Theme } from '@material-ui/core';

import { TableLoading } from '../../../shared/components/TableLoading';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { ClientContext } from '../ClientContext';
import ClientService from '../../../Services/ClientService';
import { HomeContext } from '../../Home/HomeContext';
import { TableCommon } from '../../../shared/components/TableCommon';
import { ClientModel, clientModelInitial } from '../../../Models/ClientModel';
import { BasicPage } from '../../../shared/components/BasicPage';
import { getTableColumn } from '../../../shared/operators';
import { ClientPageTabs } from '../enums/ClientPageTabs';

const useStyles = makeStyles((theme: Theme) => createStyles({
    button: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
    actionGrid: {
        padding: theme.spacing(0, 1.5)
    }
}));

export function ClientSearch() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const clientContext = React.useContext(ClientContext);

    const [showInactives, setShowInactives] = React.useState<boolean>(false);
    const [searchStatus, setSearchStatus] = React.useState<SearchStatus>(SearchStatus.INITIAL);  

    React.useEffect(() => {
        clientContext.setClient(clientModelInitial);
        clientContext.setVehicles([]);
        clientContext.setKeywords([]);
        clientContext.setSources([]);
        clientContext.setExtraParameters([]);
        clientContext.setContents([]);        
        clientContext.setFlatOrganogram([]);
        clientContext.setCutouts([]);
        clientContext.setCutoutOrganogram([]);        
        clientContext.setClientPage(ClientPageTabs.DEFAULT);
        clientContext.setEditMode(false);     
    }, []);

    React.useEffect(() => {
        fetchClients();
    }, [showInactives]);

    const tableColumns: Column<ClientModel>[] = [
        getTableColumn('Nome', 'name', 'string', 'left', { width: '60%' }, 'asc'),
        getTableColumn('ID', 'id', 'string', 'left', { width: '20%' }),
        {
            title: 'Status',
            render: rowData => (rowData.active ? 'Ativo' : 'Inativo'),
            field: 'active',
            type: 'string',
            align: 'left',
            cellStyle: { width: '15%' }
        }
    ];

    const tableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar cliente',
            onClick: (_, rowData) => { 
                clientContext.setClient(rowData);
                clientContext.setEditMode(true);
                homeContext.setPageLocation(PageLocation.CLIENT_EDIT); 
            },
        }
    ];

    const tableOptions: Options<ClientModel> = {
        sorting: true,
        filtering: true,
        toolbar: false,
        showTitle: false,
        actionsColumnIndex: -1,
        pageSizeOptions: [10, 50, 100, 200],
        pageSize: 50
    };

    const fetchClients = () => {
        setSearchStatus(SearchStatus.LOADING);
        ClientService.clientGetAll(showInactives)
            .then((res: AxiosResponse) => {
                if (res.data) {
                    clientContext.setClientList(res.data);
                    if (res.data.length > 0)
                        setSearchStatus(SearchStatus.RESULT);
                    else
                        setSearchStatus(SearchStatus.EMPTY);
                }
            })
            .catch((error: AxiosError) => {
                clientContext.setClientList([]);
                setSearchStatus(SearchStatus.EMPTY);
                homeContext.showError('Ops.. ocorreu um erro e não foi possível realizar a busca. ' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    };

    const getActions = () => {
        return (
            <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
                className={classes.actionGrid}>
                <Grid item>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showInactives}
                                onChange={() => setShowInactives(!showInactives)}
                                name="Exibir clientes inativos"
                                color="primary" />
                        }
                        label="Exibir clientes inativos" />
                </Grid>
                <Grid item>
                    <Button
                        onClick={() => { 
                            clientContext.setClient(clientModelInitial); 
                            homeContext.setPageLocation(PageLocation.CLIENT_INPUT); }}
                        size='large'
                        startIcon={<Icon color='primary'>add</Icon>}
                        className={classes.button}>
                        ADICIONAR NOVO CLIENTE
                    </Button>
                </Grid>
            </Grid>
        );
    };

    return (
        <BasicPage
            title="Consulta de clientes"
            subtitle="Para consultar um cliente, basta navegar pelas páginas da tabela abaixo.">
            <>
                {getActions()}
                <Grid item xs={12}>
                    {searchStatus == SearchStatus.LOADING && <TableLoading displayText='Carregando clientes...' />}
                    {searchStatus == SearchStatus.RESULT &&
                        <TableCommon
                            tableData={clientContext.clientList}
                            tableColumns={tableColumns}
                            emptyDataSourceMessage='Nenhum cliente encontrado.'
                            tableActions={tableActions}
                            extraOptions={tableOptions} />
                    }
                    {searchStatus == SearchStatus.EMPTY &&
                        <TableEmpty
                            displayText='Nenhum cliente foi encontrado'
                            buttonText='ADICIONAR NOVO CLIENTE'
                            pageLocation={PageLocation.CLIENT_INPUT} />
                    }
                </Grid>
            </>
        </BasicPage>
    );
}